import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link } from "@tanstack/react-router"
import {
	IconChevronRightOutline18 as ChevronRight,
	IconFingerprintOutline18 as Fingerprint,
} from "nucleo-ui-outline-18"

import { Avatar, Loader } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { SsoTarget } from "../portal-sso/sso-targets"
import { MetaInfo } from "@/components/meta-info"
import { useZeroWorkspace } from "@/lib/zero"
import { PlanPill } from "../plan-pill"
import { portalHostname } from "../portal-instance-form"
import {
	buildSsoTargets,
	endpointHost,
	ssoStateLabel,
} from "../portal-sso/sso-targets"
import { SettingsHeader } from "../settings-header"
import { SettingsContainer } from "../settings-switches/settings-container"

const DIVIDER = "border-b border-gray-200 dark:border-gray-700/80"

export function SsoSettingsPage() {
	const workspace = useZeroWorkspace()
	const [portalSso] = useQuery(queries.portalInstanceSso())

	const targets = useMemo(
		() =>
			buildSsoTargets(
				workspace,
				workspace ? portalHostname(workspace) : "",
				portalSso,
			),
		[workspace, portalSso],
	)

	return (
		<>
			<MetaInfo title="SSO" />

			<SettingsHeader
				title="SSO"
				titleSuffix={<PlanPill plan="scale" />}
				subtitle="Authenticate users on the portal with your own system. Each portal can use its own identity provider."
				docsArticle="65443d05-a78c-4979-9da3-0a43e3b35f37"
			/>

			<div className="max-w-2xl">
				<SettingsContainer className="mb-0 mt-4 px-0 pb-0">
					{!workspace ? (
						<div className="flex items-center justify-center px-4 pb-4">
							<Loader />
						</div>
					) : (
						<div className="flex flex-col">
							<div
								className={`flex items-center justify-between px-4 pb-4 ${DIVIDER}`}
							>
								<div className="text-sm font-medium">
									{targets.length} {targets.length === 1 ? "portal" : "portals"}
								</div>
							</div>
							{targets.map((target) => (
								<SsoTargetRow key={target.id} target={target} />
							))}
						</div>
					)}
				</SettingsContainer>
			</div>
		</>
	)
}

function SsoTargetRow({ target }: { target: SsoTarget }) {
	const host = endpointHost(target.tokenEndpoint)

	return (
		<Link
			to={target.href}
			className={`flex items-center gap-3 px-4 py-4 last:border-b-0 hover:bg-gray-150 dark:hover:bg-gray-800 ${DIVIDER}`}
		>
			<div className="relative">
				<Avatar
					name={target.name}
					image={target.logoUrl}
					roundness="sm"
					size="sm"
					isPortalFallback
				/>
				<div className="absolute -bottom-0 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700/90">
					<Fingerprint className="h-3 w-3 flex-none" />
				</div>
			</div>
			<div className="min-w-0 flex-1">
				<div className="flex flex-wrap items-center gap-x-2 text-sm">
					<span className="truncate font-medium">{target.name}</span>
					<span className="text-secondary">·</span>
					<span className="text-secondary">{ssoStateLabel(target)}</span>
					{host && (
						<>
							<span className="text-secondary">·</span>
							<span className="font-mono text-secondary">{host}</span>
						</>
					)}
				</div>
				<div className="truncate text-xs text-secondary">{target.url}</div>
			</div>
			<ChevronRight className="size-3 flex-none text-tertiary" />
		</Link>
	)
}
